import type Player from 'video.js/dist/types/player'
import type { VideoPlayerProps } from './types'
import videojs from 'video.js'

// Plugins are imported dynamically in initializePlugins to avoid SSR issues
// videojs-landscape-fullscreen accesses browser APIs (screen.orientation) on import

type PluginOptions = Pick<
  VideoPlayerProps,
  'seekButtons' | 'mobileUI' | 'landscapeFullscreen' | 'airPlay' | 'pip'
>

// Extended player type with plugin methods
interface PlayerWithPlugins extends Player {
  mobileUi(options?: {
    fullscreen?: {
      enterOnRotate?: boolean
      lockOnRotate?: boolean
      lockToLandscapeOnEnter?: boolean
    }
    touchControls?: {
      seekSeconds?: number
      tapTimeout?: number
      disableOnEnd?: boolean
    }
  }): void
}

// Register custom components for video.js 8
function registerCustomComponents(vjs: typeof videojs) {
  const Button = vjs.getComponent('Button')
  if (!Button) return

  // Type for button with controlText method
  type ButtonWithControlText = InstanceType<typeof Button> & {
    controlText(text: string): void
  }

  // Skip Back Button
  class SkipBackButton extends Button {
    private skipSeconds: number

    constructor(player: Player, options?: Record<string, unknown>) {
      super(player, options)
      this.skipSeconds = (options?.skipSeconds as number) ?? 10
      ;(this as unknown as ButtonWithControlText).controlText(
        `Skip back ${this.skipSeconds} seconds`,
      )
    }

    buildCSSClass() {
      return `vjs-skip-back-button vjs-control vjs-button ${super.buildCSSClass()}`
    }

    handleClick() {
      const currentTime = this.player().currentTime() ?? 0
      this.player().currentTime(Math.max(0, currentTime - this.skipSeconds))
    }
  }

  // Skip Forward Button
  class SkipForwardButton extends Button {
    private skipSeconds: number

    constructor(player: Player, options?: Record<string, unknown>) {
      super(player, options)
      this.skipSeconds = (options?.skipSeconds as number) ?? 10
      ;(this as unknown as ButtonWithControlText).controlText(
        `Skip forward ${this.skipSeconds} seconds`,
      )
    }

    buildCSSClass() {
      return `vjs-skip-forward-button vjs-control vjs-button ${super.buildCSSClass()}`
    }

    handleClick() {
      const currentTime = this.player().currentTime() ?? 0
      const duration = this.player().duration() ?? 0
      this.player().currentTime(Math.min(duration, currentTime + this.skipSeconds))
    }
  }

  // Custom AirPlay Button using native Safari API
  class AirPlayButton extends Button {
    constructor(player: Player, options?: Record<string, unknown>) {
      super(player, options)
      ;(this as unknown as ButtonWithControlText).controlText('AirPlay')

      // Only show on Safari - check for Safari-specific features
      const isSafari =
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
        // Also check for Safari on iOS
        (/iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window))

      const video = player.el().querySelector('video') as HTMLVideoElement | null
      const hasAirPlay = video && 'webkitShowPlaybackTargetPicker' in video

      if (!isSafari || !hasAirPlay) {
        this.hide()
      }
    }

    buildCSSClass() {
      return `vjs-airplay-button vjs-control vjs-button ${super.buildCSSClass()}`
    }

    handleClick() {
      const video = this.player().el().querySelector('video') as HTMLVideoElement & {
        webkitShowPlaybackTargetPicker?: () => void
      }
      if (video?.webkitShowPlaybackTargetPicker) {
        video.webkitShowPlaybackTargetPicker()
      }
    }
  }

  // Register components
  vjs.registerComponent('SkipBackButton', SkipBackButton as typeof Button)
  vjs.registerComponent('SkipForwardButton', SkipForwardButton as typeof Button)
  vjs.registerComponent('AirPlayButton', AirPlayButton as typeof Button)
}

// Register on module load
registerCustomComponents(videojs)

export async function initializePlugins(
  player: Player,
  vjs: typeof import('video.js').default,
  options: PluginOptions,
) {
  const extendedPlayer = player as PlayerWithPlugins
  const controlBar = player.getChild('controlBar')

  // Dynamically import plugins to avoid SSR issues
  // These plugins access browser APIs on import
  if (options.mobileUI) {
    await import('videojs-mobile-ui')
  }

  // AirPlay - using custom button with native Safari API
  if (options.airPlay && controlBar) {
    controlBar.addChild('AirPlayButton')
  }

  // Custom seek buttons (video.js 8 compatible)
  if (options.seekButtons && controlBar) {
    const playToggle = controlBar.getChild('PlayToggle')
    const playToggleIndex = playToggle ? controlBar.children().indexOf(playToggle) : 0

    // Add skip back button after play button
    controlBar.addChild(
      'SkipBackButton',
      { skipSeconds: options.seekButtons.back ?? 10 },
      playToggleIndex + 1,
    )

    // Add skip forward button after skip back
    controlBar.addChild(
      'SkipForwardButton',
      { skipSeconds: options.seekButtons.forward ?? 10 },
      playToggleIndex + 2,
    )
  }

  // Mobile UI
  if (options.mobileUI) {
    extendedPlayer.mobileUi({
      fullscreen: {
        enterOnRotate: false, // Disabled - causes issues with autoplay
        lockOnRotate: false,
        lockToLandscapeOnEnter: false,
      },
      touchControls: {
        seekSeconds: 10,
        tapTimeout: 300,
      },
    })
  }

  // Custom landscape fullscreen implementation
  // iOS requires user gesture for webkitEnterFullscreen, so we track interaction
  if (options.landscapeFullscreen && typeof window !== 'undefined') {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)

    if (isMobile) {
      // Track if user has interacted with the video (required for iOS fullscreen)
      let userHasInteracted = false

      // Get video element for iOS native fullscreen
      const videoEl = player.el().querySelector('video') as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void
        webkitExitFullscreen?: () => void
        webkitDisplayingFullscreen?: boolean
      }

      // Track user interaction via play/pause/touch events
      const markInteracted = () => {
        userHasInteracted = true
      }

      player.on('play', markInteracted)
      player.on('pause', markInteracted)
      player.on('touchstart', markInteracted)

      const handleOrientationChange = (e: MediaQueryListEvent) => {
        if (player.isDisposed()) return

        // e.matches is true when orientation is landscape
        if (e.matches) {
          // Entering landscape - go fullscreen
          // On iOS, only works if user has interacted with the video
          if (isIOS) {
            if (userHasInteracted && videoEl?.webkitEnterFullscreen) {
              try {
                if (!videoEl.webkitDisplayingFullscreen) {
                  videoEl.webkitEnterFullscreen()
                }
              } catch {
                // Fullscreen failed - user gesture may have expired
              }
            }
          } else {
            // Android: use standard fullscreen API
            if (!player.isFullscreen()) {
              player.requestFullscreen().catch(() => {})
            }
          }
        } else {
          // Entering portrait - exit fullscreen
          if (isIOS && videoEl?.webkitDisplayingFullscreen) {
            try {
              videoEl.webkitExitFullscreen?.()
            } catch {
              // Ignore exit errors
            }
          } else if (player.isFullscreen()) {
            player.exitFullscreen()
          }
        }
      }

      // Use matchMedia for orientation changes
      const mediaQuery = window.matchMedia('(orientation: landscape)')

      // Add listener only for changes, not current state
      mediaQuery.addEventListener('change', handleOrientationChange)

      // Cleanup on player dispose
      player.on('dispose', () => {
        mediaQuery.removeEventListener('change', handleOrientationChange)
      })
    }
  }

  // Picture-in-Picture is handled by video.js natively via controlBar options
  // No manual initialization needed - it's enabled via player options
}
